/**
 * Interview preparation, on the Applications row (post-application).
 *
 * The pipeline now carries a person all the way to "they want to talk", and
 * this is what JobCork does when it gets there. Three parts, in the order a
 * person actually uses them: the questions this posting will ask with an
 * answer already drafted from their own history, the gaps they should expect
 * to be pressed on with an honest sentence for each, and the questions to ask
 * back.
 *
 * A question the record cannot answer keeps its place and says so, because
 * knowing what is coming is worth something even when JobCork cannot write the
 * answer. Every drafted answer shows the resume's own words underneath it, so
 * the person can see what it rests on before they say it out loud.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "./auth";

interface PrepQuestion {
  question: string;
  why: string;
  answer: string | null;
  evidence: string[];
  /** Read before you say it. Every drafted answer is shown; its problems are named here. */
  warnings?: string[];
  /** Set only when the model wrote nothing for this one. */
  dropped?: "none";
}
interface PrepGap { topic: string; say: string }
interface PrepPack { questions: PrepQuestion[]; gaps: PrepGap[]; ask: string[] }
interface PrepFlag {
  claim: string;
  /** The sentence that carries the claim -- what lets a flag sit under its own answer. */
  where?: string;
  /**
   * credential / name: a figure, acronym or named thing the record lacks --
   * `claim` is the thing. unbacked: a whole sentence the record does not back
   * -- `claim` is the sentence, to be read before it is said.
   */
  kind?: "credential" | "posting" | "name" | "unbacked";
}
interface PrepView {
  pack: PrepPack | null;
  /** Which model wrote it -- so a weak pack is diagnosed at a glance. */
  model?: string | null;
  flags?: PrepFlag[];
  writtenFor?: string | null;
  stale?: boolean;
  generatedAt?: string | null;
}

const STAGE_WORDS: Record<string, string> = {
  screening: "a recruiter screen",
  interviewing: "an interview with the team",
  offer: "a final conversation",
};

function describe(err: unknown): string {
  if (err instanceof ApiError) return err.messages.join(" ");
  return err instanceof Error ? err.message : String(err);
}

/** The red lines under one answer or gap, as text -- the copy must carry them, or the phone version is the unchecked one. */
function redLines(flags: PrepFlag[], warnings: string[] = []): string[] {
  const out: string[] = [];
  const added = [...new Set(flags.filter((f) => f.kind !== "unbacked").map((f) => f.claim))];
  if (added.length > 0) out.push(`   ! Not in your resume: ${added.join(", ")}`);
  for (const f of flags.filter((f) => f.kind === "unbacked")) out.push(`   ! Your resume doesn't say this: "${f.claim}"`);
  for (const w of warnings) out.push(`   ! ${w}`);
  return out;
}

function packText(pack: PrepPack, title: string, employer: string, flagsFor: (text: string | null) => PrepFlag[]): string {
  const out: string[] = [`${title} — ${employer}`, "", "LIKELY QUESTIONS", ""];
  pack.questions.forEach((q, i) => {
    out.push(`${i + 1}. ${q.question}`);
    if (q.why) out.push(`   Why: ${q.why}`);
    out.push("", q.answer ?? "   (your record doesn't answer this one — think it through)");
    if (q.answer) out.push(...redLines(flagsFor(q.answer), q.warnings));
    out.push("");
  });
  if (pack.gaps.length > 0) {
    out.push("WHAT THEY MAY PRESS ON", "");
    for (const g of pack.gaps) out.push(`- ${g.topic}`, `  ${g.say}`, ...redLines(flagsFor(g.say)), "");
  }
  if (pack.ask.length > 0) {
    out.push("ASK THEM", "");
    for (const a of pack.ask) out.push(`- ${a}`);
  }
  return out.join("\n");
}

/**
 * What to read before saying this out loud. Two kinds of red: the things the
 * record lacks (a figure, an acronym, a name -- listed), and whole sentences
 * the record does not back (quoted, so the person sees exactly which sentence
 * to check). Then the answer's own warnings.
 */
function RedLines({ flags, warnings = [] }: { flags: PrepFlag[]; warnings?: string[] }) {
  const added = [...new Set(flags.filter((f) => f.kind !== "unbacked").map((f) => f.claim))];
  const unbacked = [...new Set(flags.filter((f) => f.kind === "unbacked").map((f) => f.claim))];
  if (added.length === 0 && unbacked.length === 0 && warnings.length === 0) return null;
  return (
    <>
      {added.length > 0 && (
        <p className="err small nomargin">Not in your resume — the model added these: {added.join(", ")}. Say them only if they're true.</p>
      )}
      {unbacked.length > 0 && (
        <div className="err small nomargin">
          Your resume doesn't say this — check it before you say it:
          <ul className="prep-unbacked">{unbacked.map((u, i) => <li key={i}>“{u}”</li>)}</ul>
        </div>
      )}
      {warnings.map((w, j) => <p className="err small nomargin" key={j}>{w}</p>)}
    </>
  );
}

export function InterviewPrep({ jobId, stage, title, employer }: { jobId: string; stage: string; title: string; employer: string }) {
  const [view, setView] = useState<PrepView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // A pack written earlier is read back on load, so it is there the morning of.
  useEffect(() => {
    api<PrepView>(`/api/applications/${jobId}/interview`).then(setView).catch(() => { /* nothing prepared yet */ });
  }, [jobId]);

  const write = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<PrepView>(`/api/applications/${jobId}/interview`, { method: "POST", body: "{}" });
      setView(r);
      setOpen(true);
    } catch (e) {
      setError(describe(e));
    } finally {
      setBusy(false);
    }
  };
  const pack = view?.pack ?? null;
  const answered = pack ? pack.questions.filter((q) => q.answer).length : 0;

  // A flag names the sentence it came from. Show it under the answer or the
  // gap that carries that sentence -- "the model added 40%" means nothing at
  // the top of the page and everything under the answer that says 40%.
  // Whatever cannot be placed stays in the line at the top.
  const allFlags = view?.flags ?? [];
  const squash = (t: string) => t.replace(/\s+/g, " ").trim().toLowerCase();
  const flagsFor = (text: string | null): PrepFlag[] => {
    if (!text) return [];
    const a = squash(text);
    return allFlags.filter((f) => f.where && a.includes(squash(f.where)));
  };
  const placed = new Set(pack ? [...pack.questions.flatMap((q) => flagsFor(q.answer)), ...pack.gaps.flatMap((g) => flagsFor(g.say))] : []);
  const unplaced = allFlags.filter((f) => !placed.has(f));

  const copy = () => {
    if (!view?.pack) return;
    navigator.clipboard?.writeText(packText(view.pack, title, employer, flagsFor))
      .then(() => setCopied(true)).catch(() => setCopied(false));
  };

  return (
    <div className="prep">
      {!open && (
        <div className="row nomargin">
          <button className="btn ghost small" type="button" disabled={busy} onClick={() => (pack ? setOpen(true) : void write())}>
            {busy ? "Preparing…" : pack ? "Open your prep" : "Prepare for the interview"}
          </button>
          {pack && view?.stale && (
            <span className="muted small">
              Written for {STAGE_WORDS[view.writtenFor ?? ""] ?? view.writtenFor}; you're past that now.
            </span>
          )}
        </div>
      )}
      {error && <p className="err small">{error}</p>}

      {open && pack && (
        <div className="prep-pack">
          <p className="muted small">
            For {STAGE_WORDS[stage] ?? "this conversation"}, from this posting and your own resume.
            {" "}{answered} of {pack.questions.length} answered from your record.
            {view?.model ? ` Written by ${view.model.split("/").pop()}.` : ""}
          </p>
          {view?.stale && (
            <p className="err small">
              This was written for {STAGE_WORDS[view.writtenFor ?? ""] ?? view.writtenFor}. Rewrite it for where you are now.
            </p>
          )}
          {unplaced.length > 0 && (
            <p className="err small">
              Read before you say it — not in your resume: {[...new Set(unplaced.map((f) => f.claim))].join(", ")}.
            </p>
          )}

          <h4 className="prep-h">Likely questions</h4>
          <ol className="prep-qs">
            {pack.questions.map((q, i) => (
              <li key={i}>
                <p className="prep-q">{q.question}</p>
                {q.why && <p className="muted small nomargin">{q.why}</p>}
                {q.answer ? (
                  <>
                    <p className="prep-a">{q.answer}</p>
                    <RedLines flags={flagsFor(q.answer)} warnings={q.warnings} />
                    {q.evidence.length > 0 && (
                      <p className="muted small nomargin">Rests on your resume: {q.evidence.map((e) => `“${e}”`).join(" · ")}</p>
                    )}
                  </>
                ) : (
                  <p className="muted small prep-a">Your record doesn't answer this one — worth thinking through before you go.</p>
                )}
              </li>
            ))}
          </ol>

          {pack.gaps.length > 0 && (
            <>
              <h4 className="prep-h">What they may press on</h4>
              <ul className="prep-gaps">
                {pack.gaps.map((g, i) => (
                  <li key={i}>
                    <strong>{g.topic}</strong>
                    <p className="nomargin">{g.say}</p>
                    <RedLines flags={flagsFor(g.say)} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {pack.ask.length > 0 && (
            <>
              <h4 className="prep-h">Ask them</h4>
              <ul className="prep-asks">{pack.ask.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </>
          )}

          <div className="row">
            <button className="btn small" type="button" onClick={copy}>{copied ? "Copied" : "Copy the lot"}</button>
            <button className="step-link small" type="button" disabled={busy} onClick={() => void write()}>{busy ? "Rewriting…" : "Rewrite it"}</button>
            <button className="step-link small" type="button" onClick={() => setOpen(false)}>Hide</button>
          </div>
        </div>
      )}
    </div>
  );
}
